import React, { useRef } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAccommodationsCta } from '../../hooks/useAccommodationsCta';
import { AccommodationCombined } from '../Accommodations/Accommodation.types';
import { EventVenue } from '@shared/utils/eventPlace';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { PartnerHotelRedirect, PartnerHotelRedirectRef } from './PartnerHotelRedirect';

interface WrapPartnerHotelRedirectProps {
  locationInfo?: EventVenue;
  eventHandle: string;
  accommodation: AccommodationCombined;
}

/**
 * HOC that provides redirect and selection logic to a child component.
 * @param WrappedComponent - The component to wrap and inject `onSelectAccommodation`
 */
export const wrapPartnerHotelRedirect = <P extends object>(
  WrappedComponent: React.ComponentType<P & { onSelectAccommodation: (event: React.MouseEvent<HTMLAnchorElement>) => void }>,
  source?: string
) => {
  return (props: P & WrapPartnerHotelRedirectProps) => {
    const { eventHandle, accommodation, locationInfo } = props;

    const partnerHotelRedirectRef = useRef<PartnerHotelRedirectRef>(null);

    const [accommodationsGuestRedirectExperiment, loadingExperiment] = useIsFeatureEnabled('accommodationsGuestRedirectExperiment');
    const accommodationsGuestRedirectExperimentEnabled = accommodationsGuestRedirectExperiment && !loadingExperiment;

    const { handleOnSelectAccommodation } = useAccommodationsCta({ eventHandle, accommodation, locationInfo });

    const onSelectAccommodation = useEventCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
      handleOnSelectAccommodation(source);
      partnerHotelRedirectRef.current?.handleOnSelectAccommodation(event);
    });

    return (
      <>
        <WrappedComponent {...props} onSelectAccommodation={onSelectAccommodation} />
        {accommodationsGuestRedirectExperimentEnabled && (
          <PartnerHotelRedirect
            ref={partnerHotelRedirectRef}
            id={accommodation.id}
            type={accommodation.type}
            displayName={accommodation.displayName}
            bookingUrl={accommodation.bookingUrl}
          />
        )}
      </>
    );
  };
};
